import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import UserContext, { Roles } from '../../../../contexts/UserContext'
import axios from '../../../../config/axios'
import styled from 'styled-components'
import { EditRowButton } from './style'
import { headerAccessesTable, accessesTableRequiredFields } from '../utils'
import { useProfileContext } from '../../../../contexts/AccessesContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ModalContext, { AccessUserForEdit } from '../../../../contexts/ModalContext'
import { parseUserStatus, UserStatus } from '../../../../types/userStatus'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import { GlobalTable, HeaderLine, Td, Th, Tr, FooterTr } from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { PeopleOutletType } from '..'
import { TypeMainOutlet } from '../..'

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: auto;
`

interface user {
  id: number
  name: string
  email: string
  phone: string
  company: string
  status: number | string
  cnpj: string
  adminUser: boolean
  permissions: string[]
  approved: string
  profileId: number | null
  FileInUser: {
    id: number
    GeneralFile: {
      id: number
      fileUrl: string
      title: string
    }
    userId: number
  }[]
}

const toAccessUserForEdit = (row: user): AccessUserForEdit => ({
  id: row.id,
  name: row.name ?? '',
  email: row.email ?? '',
  phone: row.phone ?? '',
  company: row.company ?? '',
  status: parseUserStatus(row.status),
  cnpj: row.cnpj ?? '',
  adminUser: !!row.adminUser,
  permissions: Array.isArray(row.permissions) ? row.permissions : [],
  approved: row.approved ?? '',
  profileId: row.profileId ?? null,
  attachments: (row.FileInUser ?? []).map((f) => ({
    id: f.GeneralFile.id,
    fileUrl: f.GeneralFile.fileUrl,
    title: f.GeneralFile.title,
  })),
})

const TableAccesses = () => {
  const { user } = useContext(UserContext)
  const {
    setModalActive,
    setModalSection,
    setSelectedAccessUser,
    setRefreshCallback,
  } = useContext(ModalContext)
  const [users, setUsers] = useState<user[]>([])
  const [updateUserData, setUpdateUserData] = useState(0)
  const { usersLoadQuantity, setUsersLoadQuantity, fetchProfilesWithoutAdmin } = useProfileContext()
  const { accessesFilters } = useContext(TableFiltersContext)
  const { setTabIndex } = useOutletContext<PeopleOutletType>()
  const { search } = useOutletContext<TypeMainOutlet>()
  const [hasMoreUsers, setHasMoreUsers] = useState(true)
  const [totalUsers, setTotalUsers] = useState<number | undefined>(undefined)

  const isTenantAdmin = user?.role === Roles.tenantAdmin
  const displayHeaders = useMemo(
    () => [...headerAccessesTable, ...(isTenantAdmin ? ['Ações'] : [])],
    [isTenantAdmin],
  )

  useEffect(() => {
    console.log('[AccessesTable] user.role:', user?.role, '| tenantAdmin?', isTenantAdmin, '| Roles.tenantAdmin:', Roles.tenantAdmin)
  }, [user?.role, isTenantAdmin])

  const openEditAccessUser = (row: user) => {
    setRefreshCallback(() => () => setUpdateUserData((p) => p + 1))
    setSelectedAccessUser(toAccessUserForEdit(row))
    setModalSection('editAccessUser')
    setModalActive(true)
  }

  useEffect(() => {
    setTabIndex(0)
  }, [])
  
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`, {
        params: {
          search,
          loadQuantity: usersLoadQuantity,
          filters: JSON.stringify(accessesFilters),
          excludeAdminProfile: 'true',
        }
      })
      .then((response) => {
        const resp = response.data
        const usersData = Array.isArray(resp) ? resp : resp.data || []
        const total = typeof resp === 'object' && typeof resp.total === 'number' ? resp.total : undefined

        let hasMoreData = false
        let dataToShow: any[] = usersData

        if (typeof total === 'number') {
          hasMoreData = usersData.length < total
          dataToShow = usersData
        } else {
          const legacyHasMore = usersData.length > usersLoadQuantity
          dataToShow = legacyHasMore ? usersData.slice(0, usersLoadQuantity) : usersData
          hasMoreData = legacyHasMore
        }

        setUsers(dataToShow)
        setHasMoreUsers(hasMoreData)
        setTotalUsers(total)
        console.log('👥 [ACCESSES] Loaded:', dataToShow.length, 'hasMore:', hasMoreData, 'total:', total)
      })
      .catch((error) => {
        console.log('Ocorreu um erro ao obter os dados dos users', error)
        setHasMoreUsers(false)
      })
    fetchProfilesWithoutAdmin()
  }, [updateUserData, search, usersLoadQuantity, accessesFilters])

  return (
    <>
      <TableWrapper>
        <GlobalTable>
          <tbody>
            <HeaderLine>
              {displayHeaders.map((header) => (
                <Th key={header}>{header}</Th>
              ))}
            </HeaderLine>
            {users.map((row: user) => {
              const isRowInactive = parseUserStatus(row.status) === UserStatus.INACTIVE
              return (
              <Tr
                key={row.id}
                className={isRowInactive ? 'inactiveRow' : undefined}
              >
                {accessesTableRequiredFields.map((field, index) => {
                  const rowField = (row as unknown as Record<string, unknown>)[field]
                  return (
                  <Td key={index}>
                    {field === 'adminUser'
                      ? row.profileId === 3
                        ? 'Admin'
                        : 'Comum'
                      : String(rowField ?? '')}
                  </Td>
                  )
                })}
                {isTenantAdmin && (
                  <Td>
                    <EditRowButton
                      type="button"
                      title="Editar"
                      onClick={() => openEditAccessUser(row)}
                    >
                      <Pencil size={15} />
                    </EditRowButton>
                  </Td>
                )}
              </Tr>
              )
            })}
          </tbody>
          <tfoot>
            <FooterTr>
              <Td colSpan={displayHeaders.length} style={{ textAlign: 'center', background: 'transparent' }}>
                <LoadMoreButton 
                  currentCount={users.length} 
                  totalCount={totalUsers} 
                  onLoadMore={() => {
                    const newQuantity = usersLoadQuantity + 50
                    console.log('👥 [ACCESSES] Botão clicado - incrementando de', usersLoadQuantity, 'para', newQuantity)
                    console.log('👥 [ACCESSES] Estado:', { usersLength: users.length, totalUsers, hasMore: hasMoreUsers })
                    setUsersLoadQuantity(newQuantity)
                  }} 
                  hasMore={hasMoreUsers}
                />
              </Td>
            </FooterTr>
          </tfoot>
        </GlobalTable>
      </TableWrapper>
    </>
  )
}

export default TableAccesses

